import React, { useEffect, useState } from 'react';
import { generateLeaderboard } from '../utils/data';
import { ApiService } from '../services/api';

const Leaderboard: React.FC = () => {
  const [predictions, setPredictions] = useState<any[]>([]);
  const [teamMembers, setTeamMembers] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const [predictionsRes, teamMembersRes] = await Promise.all([
          ApiService.getPredictions(),
          ApiService.getTeamMembers()
        ]);
        setPredictions(predictionsRes.data || []);
        setTeamMembers(teamMembersRes.data || []);
      } catch (err) {
        setError('Failed to load leaderboard data.');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  const leaderboardData = generateLeaderboard(predictions, teamMembers);

  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1: return '🥇';
      case 2: return '🥈';
      case 3: return '🥉';
      default: return `#${rank}`;
    }
  };

  const getRankColor = (rank: number) => {
    switch (rank) {
      case 1: return 'text-yellow-400';
      case 2: return 'text-gray-300';
      case 3: return 'text-amber-600';
      default: return 'text-gray-400';
    }
  };

  if (isLoading) {
    return <div className="text-center text-gray-300">Loading leaderboard...</div>;
  }
  if (error) {
    return <div className="text-center text-red-400">{error}</div>;
  }

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-white mb-2">🏆 Leaderboard</h2>
        <p className="text-gray-300">Team rankings based on prediction accuracy and chip management</p>
      </div>

      <div className="card">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-primary/30">
                <th className="text-left py-4 px-6 text-gray-300 font-bold">Rank</th>
                <th className="text-left py-4 px-6 text-gray-300 font-bold">Player</th>
                <th className="text-center py-4 px-6 text-gray-300 font-bold">Total Points</th>
                <th className="text-center py-4 px-6 text-gray-300 font-bold">Chips Remaining</th>
                <th className="text-center py-4 px-6 text-gray-300 font-bold">Predictions</th>
                <th className="text-center py-4 px-6 text-gray-300 font-bold">Avg Accuracy</th>
              </tr>
            </thead>
            <tbody>
              {leaderboardData.map((entry, index) => (
                <tr 
                  key={entry.teamMemberId} 
                  className="border-b border-gray-700/30 hover:bg-background-middle/50 transition-colors duration-200"
                >
                  <td className="py-4 px-6">
                    <div className={`flex items-center font-bold ${getRankColor(index + 1)}`}> 
                      {index + 1 <= 3 ? (
                        <>
                          <span className="mr-2">{getRankIcon(index + 1)}</span>
                          {index + 1}
                        </>
                      ) : (
                        <span>#{index + 1}</span>
                      )}
                    </div>
                  </td>
                  <td className="py-4 px-6">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center mr-3">
                        <span className="text-white text-sm font-bold">
                          {entry.name.charAt(0)}
                        </span>
                      </div>
                      <span className="text-white font-bold">{entry.name}</span>
                    </div>
                  </td>
                  <td className="py-4 px-6 text-center">
                    <div className="text-primary font-bold text-lg">{entry.totalPoints}</div>
                  </td>
                  <td className="py-4 px-6 text-center">
                    <div className="flex items-center justify-center">
                      <span className="mr-1">🎰</span>
                      <span className="text-white font-bold">{entry.chipsRemaining}</span>
                    </div>
                  </td>
                  <td className="py-4 px-6 text-center">
                    <div className="text-gray-300">{entry.predictionsCount}</div>
                  </td>
                  <td className="py-4 px-6 text-center">
                    <div className="flex items-center justify-center">
                      <span className={`font-bold ${
                        entry.accuracy >= 80 ? 'text-success' : 
                        entry.accuracy >= 60 ? 'text-accent' : 'text-gray-400'
                      }`}>
                        {entry.accuracy}%
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Leaderboard; 